import React from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { FinancialData } from '../utils/dataExtractor';
import { getCommonChartOptions, chartColors } from '../../../config/chartConfig';

interface FinancialChartsProps {
  data: FinancialData;
}

const FinancialCharts: React.FC<FinancialChartsProps> = ({ data }) => {
  return (
    <div className="financial-charts">
      <div className="chart-row">
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">Revenue vs Expenses</div>
            <Bar
              type="bar"
              data={{
                labels: data.periods,
                datasets: [
                  {
                    label: 'Revenue',
                    data: data.revenue,
                    backgroundColor: chartColors.blue.primary,
                    borderColor: chartColors.blue.border,
                    borderWidth: 1,
                  },
                  {
                    label: 'Expenses',
                    data: data.expenses,
                    backgroundColor: 'rgba(239, 68, 68, 0.7)',
                    borderColor: 'rgba(239, 68, 68, 1)',
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                animation: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                    labels: {
                      font: {
                        family: "'Inter', Arial, sans-serif",
                        size: 12,
                      },
                      color: '#4b5563',
                    },
                  },
                  tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    titleColor: '#1f2937',
                    bodyColor: '#4b5563',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: 10,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `$${value}`,
                      color: '#4b5563',
                      font: {
                        family: "'Inter', Arial, sans-serif",
                      },
                    },
                    grid: {
                      color: '#e5e7eb',
                    },
                  },
                  x: {
                    grid: {
                      color: '#e5e7eb',
                    },
                    ticks: {
                      color: '#4b5563',
                      font: {
                        family: "'Inter', Arial, sans-serif",
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">Profit Trend</div>
            <Line
              type="line"
              data={{
                labels: data.periods,
                datasets: [
                  {
                    label: 'Profit',
                    data: data.profit,
                    borderColor: 'rgba(16, 185, 129, 1)',
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                animation: false,
                plugins: {
                  legend: {
                    position: 'top' as const,
                    labels: {
                      font: {
                        family: "'Inter', Arial, sans-serif",
                        size: 12,
                      },
                      color: '#4b5563',
                    },
                  },
                  tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    titleColor: '#1f2937',
                    bodyColor: '#4b5563',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: 10,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `$${value}`,
                      color: '#4b5563',
                      font: {
                        family: "'Inter', Arial, sans-serif",
                      },
                    },
                    grid: {
                      color: '#e5e7eb',
                    },
                  },
                  x: {
                    grid: {
                      color: '#e5e7eb',
                    },
                    ticks: {
                      color: '#4b5563',
                      font: {
                        family: "'Inter', Arial, sans-serif",
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
      <div className="chart-row">
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">Revenue Breakdown</div>
            <Pie
              type="pie"
              data={{
                labels: ['Product A', 'Product B', 'Product C', 'Services'],
                datasets: [
                  {
                    label: 'Revenue Sources',
                    data: [40, 20, 15, 25],
                    backgroundColor: [
                      'rgba(59, 130, 246, 0.7)',
                      'rgba(16, 185, 129, 0.7)',
                      'rgba(245, 158, 11, 0.7)',
                      'rgba(99, 102, 241, 0.7)',
                    ],
                    borderColor: [
                      'rgba(59, 130, 246, 1)',
                      'rgba(16, 185, 129, 1)',
                      'rgba(245, 158, 11, 1)',
                      'rgba(99, 102, 241, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                animation: false,
                plugins: {
                  legend: {
                    position: 'right' as const,
                    labels: {
                      font: {
                        family: "'Inter', Arial, sans-serif",
                        size: 12,
                      },
                      color: '#4b5563',
                    },
                  },
                  tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    titleColor: '#1f2937',
                    bodyColor: '#4b5563',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: 10,
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="chart-col">
          <div className="chart-container">
            <div className="chart-title">Expense Breakdown</div>
            <Pie
              type="pie"
              data={{
                labels: ['Operations', 'Marketing', 'R&D', 'Admin', 'Other'],
                datasets: [
                  {
                    label: 'Expense Sources',
                    data: [35, 25, 15, 15, 10],
                    backgroundColor: [
                      'rgba(255, 99, 132, 0.7)',
                      'rgba(54, 162, 235, 0.7)',
                      'rgba(255, 206, 86, 0.7)',
                      'rgba(75, 192, 192, 0.7)',
                      'rgba(153, 102, 255, 0.7)',
                    ],
                    borderColor: [
                      'rgba(255, 99, 132, 1)',
                      'rgba(54, 162, 235, 1)',
                      'rgba(255, 206, 86, 1)',
                      'rgba(75, 192, 192, 1)',
                      'rgba(153, 102, 255, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                animation: false,
                plugins: {
                  legend: {
                    position: 'right' as const,
                    labels: {
                      font: {
                        family: "'Inter', Arial, sans-serif",
                        size: 12,
                      },
                      color: '#4b5563',
                    },
                  },
                  tooltip: {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    titleColor: '#1f2937',
                    bodyColor: '#4b5563',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: 10,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialCharts;
